import { observer } from 'mobx-react-lite'
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
    DndContext,
    DragOverlay,
    DropAnimation,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragOverEvent,
    DragEndEvent,
    DragCancelEvent,
    UniqueIdentifier,
    Modifier,
    MeasuringStrategy
} from '@dnd-kit/core'
import debugModule from 'debug'

// evertel
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { SchemaProps } from '@evertel/types'
import { Row, Col, SlidePanel, SlidePanelHeader, SlidePanelBody, useUI, Icon, Tooltip, TooltipTrigger, TooltipContent } from '@evertel/web/ui'

import { DnDBoard } from './DnDBoard'
import { DnDToolbox } from './DnDToolbox'
import { DragOverlayContent } from './DragOverlayContent'
import { SchemaDebugPanel } from './SchemaDebugPanel'
import { ItemEditForm } from '../schema-builder/elements/ItemEditForm'
import { createCollisionDetection, resetCollisionCache } from './collisionDetection'
import { createKeyboardCoordinates } from './keyboardCoordinates'
import {
    DragData,
    ParsedRow,
    ParsedItem,
    PlaceholderPosition,
    parseSchemaRows,
    isSpecialRow,
    canRowAcceptElements
} from './types'
import { MAX_ELEMENTS_PER_ROW, PLACEHOLDER_ELEMENT_ID } from './constants'
import { SchemaBuilderController } from '../../controller'
import { SchemaMediaController } from '../../controller/medias/SchemaMediaController'
import { SchemaFieldItemProps } from '../schema-builder/data/_schemaItemProps'


const debug = debugModule('app:SchemaBuilder')

// Disable drop animation - just make the overlay disappear instantly
// This prevents the awkward animation when elements reorder
const dropAnimationConfig: DropAnimation | null = null

interface SchemaBuilderDnDProps {
    schemaId: number
    schemaBuilderController: SchemaBuilderController
    schemaMediaController: SchemaMediaController
}

/**
 * Main DnD-based Schema Builder component.
 * Replaces SchemaBuilderFull with dnd-kit implementation.
 */
const SchemaBuilderDnD: React.FC<SchemaBuilderDnDProps> = observer(({
    schemaId,
    schemaBuilderController,
    schemaMediaController
}) => {

    const sessionService = useService(SessionService)
    const { isConfirmed } = useUI()

    // Refs for keyboard navigation focus targets
    const toolboxRef = useRef<HTMLDivElement>(null)
    const boardRef = useRef<HTMLDivElement>(null)

    // Track the active dragging item
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
    const [activeDragData, setActiveDragData] = useState<DragData | null>(null)

    // Track what we're hovering over (for visual feedback)
    const [overId, setOverId] = useState<UniqueIdentifier | null>(null)

    // Editor state for slide panel
    const [editorVisible, setEditorVisible] = useState(false)
    const [editingField, setEditingField] = useState<SchemaFieldItemProps | null>(null)

    // Track placeholder position to avoid unnecessary state updates
    const placeholderPositionRef = useRef<PlaceholderPosition | null>(null)

    // Track the last valid overId to prevent null flickering during layout shifts
    const lastOverIdRef = useRef<UniqueIdentifier | null>(null)

    // Track when an element was recently moved to a new container
    const recentlyMovedToNewContainer = useRef(false)

    // Local placeholder state for element drag previews
    // We use this instead of modifying the schema during drag
    const [placeholderState, setPlaceholderState] = useState<{
        rowIndex: number
        elementIndex: number
        elementType: string
    } | null>(null)

    // Get current schema based on schemaKey (root or internal schema)
    const currentSchema = useMemo(() => {
        if (!schemaBuilderController.schema) return null
        if (schemaBuilderController.schemaKey === 'root') {
            return schemaBuilderController.schema
        }
        return schemaBuilderController.schema.internalSchemas?.[schemaBuilderController.schemaKey]
    }, [schemaBuilderController.schema, schemaBuilderController.schemaKey])

    // Parse schema rows for rendering
    // Pass rootSchema so internal schemas can be resolved for list items
    const parsedRows = useMemo((): ParsedRow[] => {
        if (!currentSchema) return []
        const rootSchema = schemaBuilderController.schema
        const rows = parseSchemaRows(currentSchema, rootSchema)

        // If we have a placeholder, inject it into the appropriate row
        if (placeholderState) {
            const targetRow = rows[placeholderState.rowIndex]
            if (targetRow && canRowAcceptElements(targetRow)) {
                const placeholderElement: ParsedItem = {
                    index: placeholderState.elementIndex,
                    id: PLACEHOLDER_ELEMENT_ID,
                    type: placeholderState.elementType
                }

                // Insert placeholder at the correct position
                const newItems = [...targetRow.items]
                newItems.splice(placeholderState.elementIndex, 0, placeholderElement)

                // Reindex items
                newItems.forEach((item, idx) => {
                    item.index = idx
                })

                targetRow.items = newItems
            }
        }

        return rows
    }, [currentSchema, placeholderState])

    // Reset recentlyMovedToNewContainer after layout settles
    useEffect(() => {
        requestAnimationFrame(() => {
            recentlyMovedToNewContainer.current = false
        })
    }, [parsedRows])

    // Keyboard shortcuts for navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Don't trigger shortcuts when typing in an input/textarea or during drag
            const target = event.target as HTMLElement
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                activeId !== null
            ) {
                return
            }

            switch (event.key.toLowerCase()) {
                case 't':
                    // Focus toolbox - find first focusable element
                    if (toolboxRef.current) {
                        const firstFocusable = toolboxRef.current.querySelector<HTMLElement>(
                            '[tabindex]:not([tabindex="-1"]), button, [draggable="true"]'
                        )
                        firstFocusable?.focus()
                    }
                    break
                case 'f':
                    // Focus first row in form builder
                    if (boardRef.current) {
                        const firstRow = boardRef.current.querySelector<HTMLElement>(
                            '.sb-row button, .sb-row [tabindex]:not([tabindex="-1"])'
                        )
                        firstRow?.focus()
                    }
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [activeId])

    // Helper to find which row contains an element
    const findRowForElement = useCallback((elementId: UniqueIdentifier): ParsedRow | null => {
        const elementIdStr = String(elementId)
        for (const row of parsedRows) {
            if (canRowAcceptElements(row) && row.items.some(item => item.id === elementIdStr)) {
                return row
            }
        }
        return null
    }, [parsedRows])

    // Helper to check if a row is full
    const isRowFull = useCallback((rowIndex: number, excludeElementId?: UniqueIdentifier): boolean => {
        const row = parsedRows[rowIndex]
        if (!row || isSpecialRow(row)) return true

        const realElementCount = row.items.filter(item =>
            item.id !== PLACEHOLDER_ELEMENT_ID &&
            item.id !== excludeElementId
        ).length

        return realElementCount >= MAX_ELEMENTS_PER_ROW
    }, [parsedRows])

    // Create collision detection with current state
    const collisionDetection = useMemo(() =>
        createCollisionDetection({
            parsedRows,
            findRowForElement,
            isRowFull
        }), [parsedRows, findRowForElement, isRowFull])

    // Create keyboard coordinates with current state
    const keyboardCoordinates = useMemo(() =>
        createKeyboardCoordinates({
            parsedRows,
            isRowFull
        }), [parsedRows, isRowFull])

    // Sensors for drag detection
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5 // 5px movement before drag starts
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: keyboardCoordinates
        })
    )

    // Custom modifier to snap the drag overlay to cursor position
    const snapToCursorModifier: Modifier = useCallback(({ activatorEvent, draggingNodeRect, transform }) => {
        if (activatorEvent && draggingNodeRect) {
            const activatorCoords = activatorEvent as PointerEvent
            if (activatorCoords.clientX !== undefined) {
                return {
                    ...transform,
                    x: transform.x + activatorCoords.clientX - draggingNodeRect.left - 20,
                    y: transform.y + activatorCoords.clientY - draggingNodeRect.top - 15
                }
            }
        }
        return transform
    }, [])

    // Remove placeholder
    const removePlaceholder = useCallback(() => {
        if (placeholderPositionRef.current === null && placeholderState === null) {
            return
        }
        placeholderPositionRef.current = null
        setPlaceholderState(null)
    }, [placeholderState])

    // Handle drag start
    const handleDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event
        debug('DragStart', { activeId: active.id, data: active.data.current })

        setActiveId(active.id)
        setActiveDragData(active.data.current as DragData)
        setOverId(null)
        lastOverIdRef.current = null
    }, [])

    // Handle drag over - for real-time position updates
    const handleDragOver = useCallback((event: DragOverEvent) => {
        const { active, over, delta } = event
        const activeData = active.data.current as DragData

        debug('DragOver', { activeId: active.id, overId: over?.id })

        if (!over || !active.data.current) {
            debug('DragOver: no over target or no active data')
            setOverId(null)
            if (activeData?.type === 'element' && activeData?.fromToolbox) {
                removePlaceholder()
            } else if (!recentlyMovedToNewContainer.current) {
                removePlaceholder()
            }
            return
        }

        const overData = over.data.current as DragData | undefined
        setOverId(over.id)

        // Skip if dragging over itself
        if (active.id === over.id) {
            return
        }

        // Handle element from TOOLBOX being dragged
        if (activeData.type === 'element' && activeData.fromToolbox) {
            debug('DragOver: TOOLBOX element')

            // If hovering over placeholder, do nothing
            if (over.id === PLACEHOLDER_ELEMENT_ID) {
                return
            }

            // Determine target row
            let targetRowIndex: number | null = null
            let targetElementIndex: number | null = null

            if (overData?.type === 'element' && overData.rowIndex !== undefined) {
                targetRowIndex = overData.rowIndex
                const overElementIndex = overData.elementIndex ?? 0

                // Check if placeholder already exists
                const existingRow = parsedRows[targetRowIndex]
                const placeholderExists = existingRow && canRowAcceptElements(existingRow) &&
                    existingRow.items.some(item => item.id === PLACEHOLDER_ELEMENT_ID)

                if (placeholderExists) {
                    targetElementIndex = overElementIndex
                } else {
                    // Determine insert position based on pointer
                    const isRightOfCenter = active.rect.current.translated &&
                        over.rect &&
                        active.rect.current.translated.left > over.rect.left + over.rect.width / 2
                    targetElementIndex = isRightOfCenter ? overElementIndex + 1 : overElementIndex
                }
            }

            if (targetRowIndex === null) {
                removePlaceholder()
                return
            }

            const targetRow = parsedRows[targetRowIndex]
            if (!targetRow || isSpecialRow(targetRow)) {
                removePlaceholder()
                return
            }

            // Check if row is full
            const realElementCount = targetRow.items.filter(item => item.id !== PLACEHOLDER_ELEMENT_ID).length
            if (realElementCount >= MAX_ELEMENTS_PER_ROW) {
                removePlaceholder()
                return
            }

            // Calculate insert index
            const itemsWithoutPlaceholder = targetRow.items.filter(item => item.id !== PLACEHOLDER_ELEMENT_ID)
            const expectedInsertIndex = targetElementIndex !== null
                ? Math.min(targetElementIndex, itemsWithoutPlaceholder.length)
                : itemsWithoutPlaceholder.length

            // Check if placeholder is already in correct position
            const currentPos = placeholderPositionRef.current
            if (currentPos && currentPos.rowIndex === targetRowIndex && currentPos.elementIndex === expectedInsertIndex) {
                return
            }

            // Update placeholder
            placeholderPositionRef.current = { rowIndex: targetRowIndex, elementIndex: expectedInsertIndex }
            setPlaceholderState({
                rowIndex: targetRowIndex,
                elementIndex: expectedInsertIndex,
                elementType: activeData.elementType || 'text'
            })

            return
        }

        // Handle EXISTING element being dragged within/between rows
        if (activeData.type === 'element' && !activeData.fromToolbox) {
            debug('DragOver: EXISTING element reorder/move')

            // If we just moved to a new container, skip processing to let the layout settle
            // This prevents infinite loops when the element bounces between containers
            if (recentlyMovedToNewContainer.current) {
                debug('DragOver: Existing: recently moved, skipping to let layout settle')
                return
            }

            // Find current row containing this element (don't trust activeData.rowIndex - it's stale)
            const currentRow = findRowForElement(active.id)
            if (!currentRow) {
                debug('DragOver: element not found in any row')
                return
            }

            const currentRowIndex = currentRow.index
            const currentElementIndex = currentRow.items.findIndex(item => item.id === active.id)

            // Determine target
            let targetRowIndex: number | null = null
            let targetElementIndex: number | null = null

            if (overData?.type === 'element' && overData.rowIndex !== undefined) {
                targetRowIndex = overData.rowIndex
                const overElementIndex = overData.elementIndex ?? 0

                // For SAME ROW reordering: don't interfere - let dnd-kit's SortableContext handle it.
                // We'll commit the final position in handleDragEnd.
                if (currentRowIndex === targetRowIndex) {
                    debug('DragOver: Existing: same row reorder - letting SortableContext handle it')
                    return
                }

                // For CROSS-ROW moves: we need to handle it here because we're moving between SortableContexts
                // Use pointer position to determine insert position
                const initialRect = active.rect.current.initial
                const pointerX = initialRect ? initialRect.left + initialRect.width / 2 + delta.x : 0
                const overCenterX = over.rect ? over.rect.left + over.rect.width / 2 : 0
                const isRightOfCenter = initialRect && over.rect && pointerX > overCenterX

                targetElementIndex = isRightOfCenter ? overElementIndex + 1 : overElementIndex
                debug('DragOver: Existing: cross-row move calculation', { overElementIndex, targetElementIndex, pointerX, overCenterX, isRightOfCenter })
            }

            if (targetRowIndex === null) {
                debug('DragOver: Existing: no target row found')
                return
            }

            const targetRow = parsedRows[targetRowIndex]
            if (!targetRow || isSpecialRow(targetRow)) {
                debug('DragOver: Existing: target row not found or is special row')
                return
            }

            // Check if target row is full
            if (targetRow.items.length >= MAX_ELEMENTS_PER_ROW) {
                debug('DragOver: Existing: target row full, cannot move')
                return
            }

            // Cross-row move
            debug('DragOver: Existing: moving element to different row', { currentRowIndex, targetRowIndex, currentElementIndex, targetElementIndex })
            recentlyMovedToNewContainer.current = true

            // Use controller to move field
            const activeFieldId = String(active.id)
            schemaBuilderController.moveFieldToRow(
                currentRowIndex,
                targetRowIndex,
                `#${activeFieldId}`
            )

            return
        }

    }, [parsedRows, findRowForElement, removePlaceholder, schemaBuilderController])

    // Handle drag end
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event
        debug('DragEnd', { activeId: active.id, overId: over?.id })

        setActiveId(null)
        setActiveDragData(null)
        setOverId(null)
        lastOverIdRef.current = null
        resetCollisionCache(collisionDetection)

        if (!over) {
            removePlaceholder()
            return
        }

        placeholderPositionRef.current = null

        const activeData = active.data.current as DragData
        const overData = over.data.current as DragData | undefined

        // Handle layout items from toolbox
        if (activeData.type === 'layout' && activeData.fromToolbox) {
            let insertIndex = parsedRows.length

            // Determine insert position
            if (overData?.type === 'layout' && !overData.fromToolbox && overData.rowIndex !== undefined) {
                insertIndex = overData.rowIndex
            } else if (over.id === 'form-builder-droppable') {
                insertIndex = parsedRows.length
            }

            debug('DragEnd: adding layout', { layoutType: activeData.layoutType, insertIndex })

            // Add row first
            schemaBuilderController.addRow(insertIndex)

            // For spacer and list, add the special item to the row
            if (activeData.layoutType === 'spacer') {
                // Add spacer item to the new row
                schemaBuilderController.addFieldToRow(insertIndex, 'spacer')
            } else if (activeData.layoutType === 'list') {
                // Add list (subform) to the new row
                schemaBuilderController.addListToRow(insertIndex, 'list')
            }
        }

        // Handle element items from toolbox
        if (activeData.type === 'element' && activeData.fromToolbox) {
            // Find the row with the placeholder
            const rowWithPlaceholder = parsedRows.find(row =>
                canRowAcceptElements(row) &&
                row.items.some(item => item.id === PLACEHOLDER_ELEMENT_ID)
            )

            if (rowWithPlaceholder && activeData.elementType) {
                debug('DragEnd: adding element', { elementType: activeData.elementType, rowIndex: rowWithPlaceholder.index })
                schemaBuilderController.addFieldToRow(rowWithPlaceholder.index, activeData.elementType)
            }
        }

        // Handle reordering rows
        if (activeData.type === 'layout' && !activeData.fromToolbox &&
            overData?.type === 'layout' && !overData.fromToolbox) {

            const oldIndex = activeData.rowIndex
            const newIndex = overData.rowIndex

            if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== newIndex) {
                debug('DragEnd: reordering rows', { oldIndex, newIndex })
                schemaBuilderController.updateRow(oldIndex, newIndex)
            }
        }

        // Handle same-row element reordering
        // (Cross-row moves are already handled in handleDragOver)
        if (activeData.type === 'element' && !activeData.fromToolbox &&
            overData?.type === 'element' && !overData.fromToolbox) {

            const activeRowIndex = activeData.rowIndex
            const overRowIndex = overData.rowIndex

            // Only handle same-row reordering here
            if (activeRowIndex !== undefined && overRowIndex !== undefined &&
                activeRowIndex === overRowIndex && active.id !== over.id) {

                const row = parsedRows[activeRowIndex]
                if (row && canRowAcceptElements(row)) {
                    const oldElementIndex = row.items.findIndex(item => item.id === active.id)
                    const newElementIndex = row.items.findIndex(item => item.id === over.id)

                    if (oldElementIndex !== -1 && newElementIndex !== -1 && oldElementIndex !== newElementIndex) {
                        debug('DragEnd: same-row element reorder', { activeRowIndex, oldElementIndex, newElementIndex })
                        const activeFieldId = String(active.id)
                        schemaBuilderController.reorderFieldInRow(
                            `#${activeFieldId}`,
                            activeRowIndex,
                            oldElementIndex,
                            newElementIndex
                        )
                    }
                }
            }
        }

        // Clear placeholder
        removePlaceholder()
    }, [parsedRows, schemaBuilderController, collisionDetection, removePlaceholder])

    // Handle drag cancel
    const handleDragCancel = useCallback((event: DragCancelEvent) => {
        debug('DragCancel')
        setActiveId(null)
        setActiveDragData(null)
        setOverId(null)
        lastOverIdRef.current = null
        resetCollisionCache(collisionDetection)
        removePlaceholder()
    }, [collisionDetection, removePlaceholder])

    // Handle row deletion with confirmation
    const handleDeleteRow = useCallback(async (rowIndex: number) => {
        const confirmed = await isConfirmed({
            title: 'Delete this row?',
            message: 'Are you sure you want to delete this row? Any fields in this row will also be deleted.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })

        if (confirmed) {
            schemaBuilderController.deleteRow(rowIndex)
        }
    }, [isConfirmed, schemaBuilderController])

    // Handle element deletion with confirmation
    const handleDeleteElement = useCallback(async (rowIndex: number, fieldId: string) => {
        const confirmed = await isConfirmed({
            title: 'Delete this field?',
            message: 'Are you sure you want to delete this field? This field will also be deleted from any existing EverDocs.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })

        if (confirmed) {
            schemaBuilderController.deleteField(fieldId, rowIndex)
        }
    }, [isConfirmed, schemaBuilderController])

    // Handle editing element (includes spacers and list properties)
    const handleEditElement = useCallback((rowIndex: number, item: ParsedItem) => {
        // Check if this is a legacy spacer that needs conversion
        if (item.type === 'spacer' && item.isLegacy) {
            debug('Converting legacy spacer to field format', { rowIndex, fieldId: item.id })
            const convertedFieldId = schemaBuilderController.convertLegacySpacerToField(rowIndex)

            if (!convertedFieldId) {
                debug('Failed to convert legacy spacer')
                return
            }

            // Get the converted field data from schema
            const fields = currentSchema?.fields as Record<string, Record<string, unknown>> || {}
            const fieldData = fields[convertedFieldId]

            if (fieldData) {
                setEditingField({
                    id: convertedFieldId,
                    ...fieldData
                } as unknown as SchemaFieldItemProps)
                setEditorVisible(true)
            }
            return
        }

        // For all other elements, the item already has all the data we need
        const { index, isLegacy, ...fieldProps } = item
        setEditingField({
            ...fieldProps
        } as unknown as SchemaFieldItemProps)
        setEditorVisible(true)
    }, [currentSchema, schemaBuilderController])

    // Handle closing editor
    const handleCloseEditor = useCallback(() => {
        setEditorVisible(false)
        setEditingField(null)
    }, [])

    // Handle action callbacks from ItemEditForm (e.g., opening subform editor)
    const onActionCallback = useCallback((action: string, props: Record<string, unknown>) => {
        switch (action) {
            case 'open-form':
                handleCloseEditor()
                if (props.sKey) {
                    schemaBuilderController.setSchemaKey(props.sKey as string)
                }
                break
            default:
                break
        }
    }, [handleCloseEditor, schemaBuilderController])

    // Determine if we should hide subform option in toolbox (when editing a subform)
    const hideSubformInToolbox = schemaBuilderController.schemaKey !== 'root'

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                measuring={{
                    droppable: {
                        strategy: MeasuringStrategy.Always
                    }
                }}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {/* Keyboard Shortcuts Help - visible only when tabbed to */}
                <div className="visually-hidden" style={{left: 200}}>
                    <Tooltip placement="top">
                        <TooltipTrigger asChild>
                            <div tabIndex={0}>
                                Keyboard shortcuts available. Press T for toolbox, F for form.
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <div className="m-1 lh-sm">
                                <div className="mb-2">
                                    Press <kbd className="text-bg-secondary px-2 py-1 rounded me-1">T</kbd>
                                    to focus the Toolbox
                                </div>
                                <div>
                                    Press <kbd className="text-bg-secondary px-2 py-1 rounded me-1">F</kbd>
                                    to focus the Form Builder
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </div>

                <Row
                    valign="stretch"
                    style={{ gap: '1rem' }}>
                    {/* Form Builder Area */}
                    <Col valign="top" style={{flexGrow: 3}}>
                        <div ref={boardRef}>
                            <DnDBoard
                                rows={parsedRows}
                                activeId={activeId}
                                activeDragData={activeDragData}
                                overId={overId}
                                schemaId={schemaId}
                                onDeleteRow={handleDeleteRow}
                                onDeleteElement={handleDeleteElement}
                                onEditElement={handleEditElement}
                                onClickToEditListSchema={(internalSchemaId) => {
                                    schemaBuilderController.setSchemaKey(internalSchemaId)
                                }}
                            />
                        </div>
                    </Col>

                    {/* Toolbox */}
                    <Col
                        valign="top"
                        ref={toolboxRef}
                        style={{ flexGrow: 0, flexShrink: 0, width: 280 }}>
                        <DnDToolbox hideSubform={hideSubformInToolbox} />
                    </Col>
                </Row>

                {/* Drag Overlay */}
                <DragOverlay modifiers={[snapToCursorModifier]} dropAnimation={dropAnimationConfig}>
                    {activeId && activeDragData ? (
                        <DragOverlayContent dragData={activeDragData} />
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Debug Panel */}
            {(sessionService.debugEnabled) && (
                <SchemaDebugPanel
                    rawSchema={currentSchema as SchemaProps}
                    parsedRows={parsedRows}
                    schemaKey={schemaBuilderController.schemaKey}
                />
            )}

            {/* Editor Slide Panel */}
            <SlidePanel
                visible={editorVisible}
                onHide={handleCloseEditor}
                backdrop="clear"
                placement="end"
                style={{ width: 450 }}
            >
                <SlidePanelHeader
                    title={`Edit ${editingField?.type === 'list' ? 'Sub Form' : editingField?.type || 'Element'}`}
                />
                <SlidePanelBody>
                    {editingField && (
                        <ItemEditForm
                            field={editingField}
                            onChange={(data) => schemaBuilderController.updateField(data)}
                            onChangeListLayoutField={(data) => schemaBuilderController.updateListLayoutField(data)}
                            schemaMediaController={schemaMediaController}
                            onAction={onActionCallback}
                        />
                    )}
                </SlidePanelBody>
            </SlidePanel>
        </>
    )
})

export { SchemaBuilderDnD }
